/**
 * Phase 18 — Admin Partner Marketing Kit page.
 *
 * Route: /admin/partners/marketing-kit
 *
 * PINNED USER RULE (Feb 2026):
 *   Phase 18 NEVER sends emails / SMS / social posts, NEVER publishes
 *   public pages. This page is preview / copy / download only.
 *
 * Surfaces:
 *   1. Safety banner — "preview only · no emails / SMS / public pages".
 *   2. Partner selector — only active PWS partners.
 *   3. Booking-link card with copyable URL.
 *   4. Social post copy (5 calm blocks).
 *   5. Email / SMS copy drafts (4 calm blocks).
 *   6. One-page partner flyer (open in new tab).
 *   7. Brand / disclosure block.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Loader2, ShieldAlert,
  Megaphone, Copy, Check, ExternalLink, FileText, Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { EmailSignatureBannerCard } from "@/components/partner/EmailSignatureBannerCard";
import { FacebookPostKitCard } from "@/components/partner/FacebookPostKitCard";


function useClipboard() {
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = useCallback(async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey((c) => (c === key ? null : c)), 1500);
    } catch (e) {
      toast.error("Copy failed — select and copy manually.");
    }
  }, []);
  return { copiedKey, copy };
}


function CopyButton({ value, copiedKey, copyKey, copy, label = "Copy" }) {
  const isCopied = copiedKey === copyKey;
  return (
    <button
      onClick={() => copy(copyKey, value)}
      data-testid={`ph18-copy-${copyKey}`}
      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-slate-200 hover:bg-slate-50"
    >
      {isCopied ? (
        <Check className="w-3 h-3 text-emerald-700" />
      ) : (
        <Copy className="w-3 h-3" />
      )}
      {isCopied ? "Copied" : label}
    </button>
  );
}


// ── Partner selector ────────────────────────────────────────────────────
function PartnerSelector({ partners, partnerId, onChange }) {
  return (
    <section
      data-testid="ph18-partner-selector"
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-2">
        <Megaphone className="w-3.5 h-3.5 text-blue-700" /> Select partner
      </div>
      <select
        data-testid="ph18-partner-select"
        value={partnerId || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="text-sm w-full border border-slate-200 rounded-md px-2 py-2 bg-white"
      >
        <option value="">— Select an active PWS partner —</option>
        {partners.map((p) => (
          <option key={p.id} value={p.id}>
            {p.business_name} · {p.franchise_region || "—"} ({p.partner_slug})
          </option>
        ))}
      </select>
      <p className="mt-2 text-[10px] text-slate-400 italic">
        Only active partners are listed. Draft / inactive / suspended
        partners are intentionally excluded.
      </p>
    </section>
  );
}


// ── Booking-link card ───────────────────────────────────────────────────
function BookingLinkCard({ card, copiedKey, copy, partnerId }) {
  if (!card) return null;
  const qr = card.qr_code;
  return (
    <section
      data-testid="ph18-booking-card"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
          {card.label}
        </div>
        <CopyButton
          value={card.booking_url}
          copyKey="booking_url"
          copiedKey={copiedKey}
          copy={copy}
          label="Copy URL"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Trading name
          </div>
          <div className="font-semibold text-slate-800">{card.trading_name}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Region
          </div>
          <div className="text-slate-800">{card.region || "—"}</div>
        </div>
        <div className="sm:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            Booking URL
          </div>
          <a
            href={card.booking_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ph18-booking-url"
            className="text-blue-700 hover:underline break-all font-mono text-xs"
          >
            {card.booking_url}
          </a>
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-600">{card.description}</p>
      <div
        data-testid="ph18-booking-card-disclosure"
        className="mt-3 text-[11px] text-slate-500 italic"
      >
        {card.disclosure}
      </div>
      {qr && (
        <div
          data-testid="ph19-qr-card"
          className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-4 flex items-center gap-4 flex-wrap overflow-hidden"
        >
          <img
            src={qr.png_data_url}
            alt="Partner booking QR code"
            data-testid="ph19-qr-image"
            width={144}
            height={144}
            className="rounded-md bg-white p-1 border border-sky-200"
          />
          <div className="flex-1 min-w-[180px]">
            <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700">
              Partner Booking QR Code · v{qr.version}
            </div>
            <div className="text-xs text-slate-700 mt-1">
              {qr.label || "Book a water test"}
            </div>
            <div className="text-[10px] text-slate-500 font-mono break-all mt-1">
              {qr.booking_url}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <a
                href={qr.png_data_url}
                download={`pws-qr-${partnerId}.png`}
                data-testid="ph19-qr-download-png"
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-sky-200 hover:bg-white"
              >
                Download PNG
              </a>
              <a
                href={qr.svg_data_url}
                download={`pws-qr-${partnerId}.svg`}
                data-testid="ph19-qr-download-svg"
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-sky-200 hover:bg-white"
              >
                Download SVG
              </a>
              <CopyButton
                value={qr.png_data_url}
                copyKey="qr_png_data_url"
                copiedKey={copiedKey}
                copy={copy}
                label="Copy data URL"
              />
            </div>
            <div className="mt-2 text-[10px] text-slate-500 italic">
              QR code points to the public booking URL only. No admin or
              internal endpoints.
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


// ── Social posts ────────────────────────────────────────────────────────
function SocialPostsCard({ posts, copiedKey, copy }) {
  if (!posts || !posts.length) return null;
  return (
    <section
      data-testid="ph18-social-posts"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-3">
        Social Post Copy
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {posts.map((p) => (
          <div
            key={p.slug}
            data-testid={`ph18-social-${p.slug}`}
            className="rounded-lg border border-slate-200 bg-slate-50 p-3"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs font-semibold text-slate-800">
                {p.title}
              </div>
              <CopyButton
                value={p.body}
                copyKey={`social_${p.slug}`}
                copiedKey={copiedKey}
                copy={copy}
              />
            </div>
            <div className="text-xs text-slate-700 whitespace-pre-line">
              {p.body}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


// ── Email / SMS drafts ──────────────────────────────────────────────────
function EmailSMSCard({ drafts, copiedKey, copy }) {
  if (!drafts || !drafts.length) return null;
  return (
    <section
      data-testid="ph18-email-sms"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-3">
        <Mail className="w-3.5 h-3.5 text-blue-700" /> Email / SMS Copy Drafts
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {drafts.map((d) => (
          <div
            key={d.slug}
            data-testid={`ph18-email-${d.slug}`}
            className="rounded-lg border border-slate-200 bg-slate-50 p-3"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-xs font-semibold text-slate-800">
                {d.title}
              </div>
              <CopyButton
                value={`Subject: ${d.subject}\n\n${d.body}`}
                copyKey={`email_${d.slug}`}
                copiedKey={copiedKey}
                copy={copy}
              />
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Subject
            </div>
            <div className="text-xs text-slate-800 mb-2">{d.subject}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Body
            </div>
            <div className="text-xs text-slate-700 whitespace-pre-line">
              {d.body}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[10px] text-slate-400 italic">
        These drafts are for the partner to copy and use manually. Phase 18
        never sends emails or SMS automatically.
      </p>
    </section>
  );
}


// ── Printable flyer ─────────────────────────────────────────────────────
function FlyerCard({ flyer, partnerId, token }) {
  const [opening, setOpening] = useState(false);

  // The flyer endpoint requires Bearer auth. Anchor-tag `target="_blank"`
  // doesn't send Authorization headers, so a plain click gets 401/405
  // and shows a blank page. Fetch the HTML with our authenticated axios
  // instance, wrap it in a blob URL, then open THAT in a new tab.
  const openFlyer = async () => {
    if (!flyer) return;
    setOpening(true);
    try {
      const resp = await api.get(flyer.html_endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/html" }));
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.error("Popup blocked — allow popups for this site to open the flyer.");
      }
      // Revoke the URL after a few seconds so we don't leak. The new
      // tab has already snapshot the HTML, so revocation doesn't
      // affect the rendered page.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to open flyer");
    } finally {
      setOpening(false);
    }
  };

  if (!flyer) return null;

  return (
    <section
      data-testid="ph18-flyer-card"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
          <FileText className="w-3.5 h-3.5 text-blue-700" /> {flyer.label}
        </div>
        <button
          type="button"
          onClick={openFlyer}
          disabled={opening}
          data-testid="ph18-flyer-open"
          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline disabled:opacity-50"
        >
          {opening ? "Opening…" : "Open in new tab"} <ExternalLink className="w-3 h-3" />
        </button>
      </div>
      <p className="text-xs text-slate-600">
        {flyer.download_label}. The flyer is a single HTML page — open it in
        a new tab and use your browser&apos;s Print → Save as PDF for a
        printable version. The page contains no scripts and no trackers.
      </p>
    </section>
  );
}


// ── Brand / disclosure block ────────────────────────────────────────────
function BrandDisclosureCard({ block, copiedKey, copy }) {
  if (!block) return null;
  return (
    <section
      data-testid="ph18-brand-disclosure"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-3">
        Brand / Disclosure Block
      </div>
      <div className="space-y-3">
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">
              Short strap (use under every social post / email)
            </div>
            <CopyButton
              value={block.short_strap}
              copyKey="brand_short"
              copiedKey={copiedKey}
              copy={copy}
            />
          </div>
          <div
            className="text-sm text-slate-800"
            data-testid="ph18-brand-short"
          >
            {block.short_strap}
          </div>
        </div>
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">
              Long strap (use on the flyer / website)
            </div>
            <CopyButton
              value={block.long_strap}
              copyKey="brand_long"
              copiedKey={copiedKey}
              copy={copy}
            />
          </div>
          <div
            className="text-sm text-slate-800"
            data-testid="ph18-brand-long"
          >
            {block.long_strap}
          </div>
        </div>
        <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
              Not-a-quote reminder
            </div>
            <CopyButton
              value={block.not_a_quote}
              copyKey="brand_not_a_quote"
              copiedKey={copiedKey}
              copy={copy}
            />
          </div>
          <div
            className="text-sm text-slate-800"
            data-testid="ph18-brand-not-a-quote"
          >
            {block.not_a_quote}
          </div>
        </div>
      </div>
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
export default function AdminPWSMarketingKit() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState(null);
  const [kit, setKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(false);
  const { copiedKey, copy } = useClipboard();

  // Load partner list on mount
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/pws-marketing-kit/partners");
        if (!alive) return;
        setPartners(data.partners || []);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Load kit when partner is selected
  useEffect(() => {
    if (!partnerId) {
      setKit(null);
      return;
    }
    let alive = true;
    (async () => {
      setKitLoading(true);
      try {
        const { data } = await api.get(
          `/admin/pws-marketing-kit/${encodeURIComponent(partnerId)}`,
        );
        if (!alive) return;
        setKit(data);
      } catch (e) {
        if (alive) {
          toast.error(formatApiError(e) || "Kit load failed.");
          setKit(null);
        }
      } finally {
        if (alive) setKitLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [partnerId]);

  const selectedPartnerLabel = useMemo(() => {
    const p = partners.find((x) => x.id === partnerId);
    return p ? `${p.business_name} (${p.partner_slug})` : null;
  }, [partners, partnerId]);

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-marketing-kit-page"
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          data-testid="ph18-back-to-network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-blue-700">
              Aware Partner Network · Phase 18
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner Marketing Kit
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Generate calm, factual marketing assets for active PWS
              partners — booking-link card, social post copy, email / SMS
              drafts, a one-page partner flyer, and the approved
              brand / disclosure block.{" "}
              <strong className="text-slate-700">
                Phase 18 never sends emails, SMS, social posts or
                publishes public pages.
              </strong>
            </p>
            {selectedPartnerLabel && (
              <p
                data-testid="ph18-selected-partner-label"
                className="mt-1 text-xs text-blue-700 font-semibold"
              >
                Showing kit for: {selectedPartnerLabel}
              </p>
            )}
          </div>
          <button
            onClick={() => { setPartnerId(null); setKit(null); }}
            disabled={!partnerId}
            data-testid="ph18-clear-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset
          </button>
        </div>

        {/* Safety banner */}
        <div
          data-testid="ph18-safety-banner"
          className="mt-5 rounded-xl bg-blue-50 border border-blue-200 p-4 flex items-start gap-3 text-sm text-blue-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Preview only.</strong> No emails, SMS or social posts
            are sent. No public pages are published. All copy uses
            approved calm wording, includes the Aware / PWS disclosure,
            avoids medical / scare / NATA claims, and never defaults to
            Soda Ash or Reverse Osmosis recommendations. BioCleanse
            remains supplementary only.
          </div>
        </div>

        {loading ? (
          <div
            data-testid="ph18-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading partners…
          </div>
        ) : error ? (
          <div
            data-testid="ph18-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : (
          <>
            <PartnerSelector
              partners={partners}
              partnerId={partnerId}
              onChange={setPartnerId}
            />
            {kitLoading && (
              <div
                data-testid="ph18-kit-loading"
                className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
              >
                <Loader2 className="w-5 h-5 animate-spin" /> Loading kit…
              </div>
            )}
            {kit && !kitLoading && (
              <div data-testid="ph18-kit-body">
                <BookingLinkCard
                  card={kit.booking_link_card}
                  copiedKey={copiedKey}
                  copy={copy}
                  partnerId={partnerId}
                />
                <SocialPostsCard
                  posts={kit.social_posts}
                  copiedKey={copiedKey}
                  copy={copy}
                />
                <EmailSMSCard
                  drafts={kit.email_sms_drafts}
                  copiedKey={copiedKey}
                  copy={copy}
                />
                <FlyerCard
                  flyer={kit.printable_flyer}
                  partnerId={partnerId}
                />
                <BrandDisclosureCard
                  block={kit.brand_disclosure_block}
                  copiedKey={copiedKey}
                  copy={copy}
                />
                {/* Jun 2026 — email signature verification view */}
                <div className="mt-6">
                  <EmailSignatureBannerCard
                    sig={kit.email_signature}
                    testidPrefix="admin-email-signature"
                  />
                </div>
                {/* Jun 2026 — Facebook post kit verification view */}
                <div className="mt-6">
                  <FacebookPostKitCard
                    kit={kit.facebook_post}
                    testidPrefix="admin-facebook-post"
                  />
                </div>
                <p
                  data-testid="ph18-kit-compliance"
                  className="mt-4 text-[10px] text-slate-400 italic"
                >
                  {kit.compliance_note}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </PortalShell>
  );
}
